import type { ItineraryStop } from "../../shared/api/types";
import type { VoyageStopOverride } from "../../shared/api/endpoints";

export interface StopIdentity {
  portExternalRef: string;
  portFunc: string;
  occurrenceIndex: number;
  dummySource: "" | "previous" | "next";
}

function dsOf(stop: ItineraryStop): "" | "previous" | "next" {
  if (!stop.isDummy) return "";
  return stop.dummySource === "previous" || stop.dummySource === "next" ? stop.dummySource : "";
}
function refOf(stop: ItineraryStop): string {
  return stop.port?.externalRef ?? "";
}

// 1-indexed N-th stop matching (portExternalRef, portFunc, dummySource)
// in itinerary array order.
export function stopIdentity(stop: ItineraryStop, stops: readonly ItineraryStop[]): StopIdentity {
  const ref = refOf(stop), func = stop.portFunc ?? "", ds = dsOf(stop);
  let occ = 0;
  for (const s of stops) {
    if (refOf(s) === ref && (s.portFunc ?? "") === func && dsOf(s) === ds) {
      occ += 1;
      if (s === stop) break;
    }
  }
  return { portExternalRef: ref, portFunc: func, occurrenceIndex: occ, dummySource: ds };
}

const hasIdentity = (o: VoyageStopOverride): boolean =>
  typeof o.portExternalRef === "string" && o.portExternalRef.length > 0;

// Stamp `row` with the content identity AND storage key of `stop` — used
// on every save so the row tracks the bound stop, not wherever the row
// came from. Critical for reattach: the caller passes the orphan row +
// the target stop, and the storage key (stopOrder/dummySource) must
// follow the target, not linger at the orphan's stale position.
export function stampIdentity(
  row: VoyageStopOverride,
  stop: ItineraryStop,
  stops: readonly ItineraryStop[],
): VoyageStopOverride {
  const id = stopIdentity(stop, stops);
  return {
    ...row,
    portExternalRef: id.portExternalRef,
    portFunc: id.portFunc,
    occurrenceIndex: id.occurrenceIndex,
    stopOrder: stop.order,
    dummySource: id.dummySource,
  };
}

// True iff `override` carries >=1 stevedore session and the session
// span [min(start), max(end)] overlaps the stop's [arrivalLocal,
// departureLocal] window. Same overlap math as the backend backfill
// (backend/overrides.ts backfillIdentities): strict Math.min(spanEnd,
// dep) > Math.max(spanStart, arr). False when sessions are empty or
// the stop's window doesn't parse.
export function sessionsOverlapStop(override: VoyageStopOverride, stop: ItineraryStop): boolean {
  const sess = override.stevedoreSessions;
  if (!sess || sess.length === 0) return false;
  const spanStart = Math.min(...sess.map((s) => Date.parse(s.start)));
  const spanEnd = Math.max(...sess.map((s) => Date.parse(s.end)));
  if (!stop.arrivalLocal || !stop.departureLocal) return false;
  const a = Date.parse(stop.arrivalLocal), d = Date.parse(stop.departureLocal);
  if (!Number.isFinite(a) || !Number.isFinite(d)) return false;
  return Math.min(spanEnd, d) > Math.max(spanStart, a);
}

// Resolve each override to a live stop. Identity rows degrade
// (exact -> relax occurrence -> LOCODE-5) and orphan only when the
// terminal+func is truly absent. Legacy rows (no identity) fall back to
// the old (stopOrder, dummySource) match; if the row also carries
// stevedore sessions, the positional stop's window must overlap them or
// the row has been silently shifted by a re-cut and is orphaned instead.
// Legacy rows with no sessions (volume-only, no date signal) keep the
// old behaviour: bind positionally, never orphan.
//
// A stop can only be bound ONCE. If a degraded match (occurrence-relax
// or LOCODE-5) or a legacy positional match would land on a stop that's
// already claimed by an earlier override, treat it as no match instead
// of silently double-binding — the loser becomes an orphan (identity
// rows) so downstream `byStop`-style consumers never see two overrides
// resolve to the same stop.
export function resolveOverrides(
  overrides: readonly VoyageStopOverride[],
  stops: readonly ItineraryStop[],
): { bindings: { override: VoyageStopOverride; stop: ItineraryStop }[]; orphans: VoyageStopOverride[] } {
  // Fast path: the vast majority of voyages have no overrides, yet the
  // calendar stripe marker resolves per bar per pan frame. Bail before
  // the O(n²) stopIdentity pass below when there's nothing to bind.
  if (overrides.length === 0) return { bindings: [], orphans: [] };
  const ids = stops.map((s) => stopIdentity(s, stops));
  const bindings: { override: VoyageStopOverride; stop: ItineraryStop }[] = [];
  const orphans: VoyageStopOverride[] = [];
  const boundStops = new Set<ItineraryStop>();

  for (const o of overrides) {
    if (!hasIdentity(o)) {
      const ds = o.dummySource ?? "";
      const legacy = stops.find((s) => s.order === o.stopOrder && dsOf(s) === ds && !boundStops.has(s));
      if (o.stevedoreSessions && o.stevedoreSessions.length > 0) {
        if (legacy && sessionsOverlapStop(o, legacy)) { bindings.push({ override: o, stop: legacy }); boundStops.add(legacy); }
        else orphans.push(o);
      } else if (legacy) {
        bindings.push({ override: o, stop: legacy });
        boundStops.add(legacy);
      } // else: unbound, NOT an orphan (no date signal to judge it by)
      continue;
    }
    const ref = o.portExternalRef as string, func = o.portFunc ?? "", ds = o.dummySource ?? "";
    const occ = o.occurrenceIndex ?? 1;
    const at = (pred: (s: ItineraryStop, id: StopIdentity) => boolean): ItineraryStop | undefined => {
      for (let i = 0; i < stops.length; i++) if (pred(stops[i]!, ids[i]!) && !boundStops.has(stops[i]!)) return stops[i];
      return undefined;
    };
    const hit =
      at((s, id) => id.portExternalRef === ref && s.portFunc === func && id.dummySource === ds && id.occurrenceIndex === occ)
      ?? at((s, id) => id.portExternalRef === ref && s.portFunc === func && id.dummySource === ds)
      ?? at((s, id) => id.portExternalRef.slice(0, 5) === ref.slice(0, 5) && s.portFunc === func && id.dummySource === ds);
    if (hit) { bindings.push({ override: o, stop: hit }); boundStops.add(hit); }
    else orphans.push(o);
  }
  return { bindings, orphans };
}

// Single definition of "these two override rows are the same slot" —
// used by rebuildOverrideList (replace-on-save) and deleteOverride
// (remove-by-identity) so the two paths can't drift apart.
export function sameIdentity(a: VoyageStopOverride, b: VoyageStopOverride): boolean {
  if (hasIdentity(a) && hasIdentity(b)) {
    return a.portExternalRef === b.portExternalRef
      && (a.portFunc ?? "") === (b.portFunc ?? "")
      && (a.occurrenceIndex ?? 1) === (b.occurrenceIndex ?? 1)
      && (a.dummySource ?? "") === (b.dummySource ?? "");
  }
  return a.stopOrder === b.stopOrder && (a.dummySource ?? "") === (b.dummySource ?? "");
}

function isAllNull(r: VoyageStopOverride): boolean {
  return (!r.stevedoreSessions || r.stevedoreSessions.length === 0)
    && r.loadUnits === null && r.dischargeUnits === null && r.shiftingUnits === null
    && !r.loadSegments && !r.dischargeSegments && !r.shiftingSegments;
}

// Replace the row sharing `row`'s identity (or drop it when cleared),
// keeping the list sorted. Mirrors the backend's drop-empty behaviour.
export function rebuildOverrideList(
  existing: readonly VoyageStopOverride[],
  row: VoyageStopOverride,
): VoyageStopOverride[] {
  const next = existing.filter((r) => !sameIdentity(r, row));
  if (!isAllNull(row)) next.push(row);
  next.sort((a, b) => a.stopOrder - b.stopOrder || (a.dummySource ?? "").localeCompare(b.dummySource ?? ""));
  return next;
}

// Best reattach target: the working stop whose [arrival, departure]
// window overlaps the override's stevedore session span. Null when the
// override has no sessions or nothing overlaps.
export function suggestReattachStop(
  override: VoyageStopOverride,
  stops: readonly ItineraryStop[],
): ItineraryStop | null {
  const sess = override.stevedoreSessions;
  if (!sess || sess.length === 0) return null;
  let best: ItineraryStop | null = null, bestOverlap = 0;
  for (const s of stops) {
    if (s.portFunc !== "L" && s.portFunc !== "D") continue;
    if (!sessionsOverlapStop(override, s)) continue;
    const spanStart = Math.min(...sess.map((x) => Date.parse(x.start)));
    const spanEnd = Math.max(...sess.map((x) => Date.parse(x.end)));
    const a = Date.parse(s.arrivalLocal as string), d = Date.parse(s.departureLocal as string);
    const overlap = Math.min(spanEnd, d) - Math.max(spanStart, a);
    if (overlap > bestOverlap) { bestOverlap = overlap; best = s; }
  }
  return best;
}
